/**
 * Frontend Sentry bootstrap. No-op when `REACT_APP_SENTRY_DSN` is unset.
 *
 * Call once in `src/index.js` BEFORE rendering React:
 *
 *     import { initSentry } from './lib/sentry';
 *     initSentry();
 *
 * The DSN is provided via the Emergent deployment UI as
 * `REACT_APP_SENTRY_DSN`. Do NOT commit a real DSN to the repo.
 *
 * Sentry is loaded at RUNTIME from the official browser CDN (jsdelivr)
 * rather than bundled into the webpack output. This keeps the default
 * bundle size unchanged for non-production builds, and avoids the need
 * to install `@sentry/react` as a hard dependency until you actually
 * deploy with a DSN.
 */

const SENTRY_CDN_VERSION = '7.118.0';

function _loadSentryFromCDN() {
  if (window.Sentry) return Promise.resolve(window.Sentry);
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://browser.sentry-cdn.com/${SENTRY_CDN_VERSION}/bundle.tracing.min.js`;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(window.Sentry);
    script.onerror = () => reject(new Error('Failed to load Sentry CDN bundle'));
    document.head.appendChild(script);
  });
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Dev / preview environments — quietly skip.
    return false;
  }

  _loadSentryFromCDN()
    .then((Sentry) => {
      if (!Sentry || typeof Sentry.init !== 'function') {
        // eslint-disable-next-line no-console
        console.warn('[sentry] CDN bundle loaded but Sentry global missing');
        return;
      }
      Sentry.init({
        dsn,
        environment: process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || 'production',
        release: process.env.REACT_APP_RELEASE_VERSION || undefined,
        tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.1),
        // Strip likely-PII before sending.
        beforeSend(event) {
          if (event?.request?.cookies) {
            event.request.cookies = '[redacted]';
          }
          if (event?.request?.headers) {
            ['authorization', 'cookie'].forEach((h) => {
              if (event.request.headers[h]) event.request.headers[h] = '[redacted]';
            });
          }
          return event;
        },
      });
    })
    .catch((err) => {
      // Loading the SDK should never crash the app.
      // eslint-disable-next-line no-console
      console.warn('[sentry] failed to initialize:', err);
    });
  return true;
}
